import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { conferenceApi } from '@/_services/api/conference';
import ConferenceChatInput from './ConferenceChatInput';
import SenderMessage from '../../Teams/SenderMessage';
import ReceiverMessage from '../../Teams/ReceiverMessage';
import { VscLoading } from 'react-icons/vsc';

const MESSAGE_PAGE_SIZE = 10;
const MESSAGE_FETCH_INTERVAL = 10000;

const mergeMessages = (existingMessages, incomingMessages) => {
    const messagesById = new Map();
    [...existingMessages, ...incomingMessages].forEach((message) => {
        messagesById.set(message.id || message._id, message);
    });
    return [...messagesById.values()].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
};

const ConferenceChat = ({ conferenceId, channelInfo }) => {
    const currentUser = useSelector((state) => state.user.user);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingOlder, setLoadingOlder] = useState(false);
    const [sending, setSending] = useState(false);
    const [sendError, setSendError] = useState('');
    const [fetchError, setFetchError] = useState('');
    const listRef = useRef(null);
    const scrollContentRef = useRef(null);
    const messageRefs = useRef({});
    const nextPageRef = useRef(2);
    const loadingOlderRef = useRef(false);
    const hasOlderMessagesRef = useRef(false);
    const pendingScrollRestoreRef = useRef(null);
    const shouldStayAtBottomRef = useRef(true);
    const lastRenderedMessageIdRef = useRef(null);
    const roomId = channelInfo?._id || channelInfo?.id;
    const userId = currentUser?.id || currentUser?._id;

    const fetchMessages = useCallback(async () => {
        if (!roomId) return;
        try {
            const response = await conferenceApi.getChannelMessages(roomId, 1, MESSAGE_PAGE_SIZE);
            const latestMessages = [...(response?.results || [])].reverse();
            setMessages((currentMessages) => mergeMessages(currentMessages, latestMessages));
            hasOlderMessagesRef.current = (response?.page || 1) < (response?.totalPages || 1);
            setFetchError('');
        } catch (error) {
            if (error?.name === 'AbortError') return;
            setFetchError(error?.message || 'Could not load channel messages. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [roomId]);

    const pollForNewMessages = useCallback(async () => {
        if (!roomId) return;
        try {
            const response = await conferenceApi.getChannelMessages(roomId, 1, 1);
            const latestMessage = response?.results?.[0];
            const fetchedLatestMessageId = latestMessage?.id || latestMessage?._id;
            if (fetchedLatestMessageId && fetchedLatestMessageId !== lastRenderedMessageIdRef.current) {
                await fetchMessages();
            } else {
                setFetchError('');
            }
        } catch (error) {
            setFetchError(error?.message || 'Could not check for new channel messages. Please try again.');
        }
    }, [roomId, fetchMessages]);

    const loadOlderMessages = useCallback(async () => {
        if (!roomId || !hasOlderMessagesRef.current || loadingOlderRef.current) return;
        const list = listRef.current;
        if (!list) return;

        loadingOlderRef.current = true;
        setLoadingOlder(true);
        shouldStayAtBottomRef.current = false;
        pendingScrollRestoreRef.current = { height: list.scrollHeight, top: list.scrollTop };
        try {
            const page = nextPageRef.current;
            const response = await conferenceApi.getChannelMessages(roomId, page, MESSAGE_PAGE_SIZE);
            const olderMessages = [...(response?.results || [])].reverse();
            if (olderMessages.length > 0) {
                setMessages((currentMessages) => mergeMessages(currentMessages, olderMessages));
                nextPageRef.current = page + 1;
            }
            hasOlderMessagesRef.current = page < (response?.totalPages || page);
            setFetchError('');
            if (olderMessages.length === 0) pendingScrollRestoreRef.current = null;
        } catch (error) {
            if (error?.name === 'AbortError') return;
            pendingScrollRestoreRef.current = null;
            setFetchError(error?.message || 'Could not load earlier messages. Please try again.');
        } finally {
            loadingOlderRef.current = false;
            setLoadingOlder(false);
        }
    }, [roomId]);

    useEffect(() => {
        setLoading(true);
        fetchMessages();
        const interval = window.setInterval(pollForNewMessages, MESSAGE_FETCH_INTERVAL);
        return () => window.clearInterval(interval);
    }, [fetchMessages, pollForNewMessages]);

    useEffect(() => {
        const list = listRef.current;
        const content = scrollContentRef.current;
        if (!list || !content || typeof ResizeObserver === 'undefined') return undefined;

        const keepLatestVisible = () => {
            if (shouldStayAtBottomRef.current) {
                list.scrollTo({ top: list.scrollHeight, behavior: 'smooth' });
            }
        };
        const observer = new ResizeObserver(keepLatestVisible);
        observer.observe(list);
        observer.observe(content);
        return () => observer.disconnect();
    }, []);

    const latestMessageId = messages[messages.length - 1]?.id || messages[messages.length - 1]?._id;

    useLayoutEffect(() => {
        const list = listRef.current;
        if (!list) return;
        if (pendingScrollRestoreRef.current) {
            const { height, top } = pendingScrollRestoreRef.current;
            list.scrollTop = top + list.scrollHeight - height;
            pendingScrollRestoreRef.current = null;
        } else if (
            latestMessageId &&
            latestMessageId !== lastRenderedMessageIdRef.current &&
            shouldStayAtBottomRef.current
        ) {
            list.scrollTo({
                top: list.scrollHeight,
                behavior: lastRenderedMessageIdRef.current ? 'smooth' : 'auto',
            });
        }
        lastRenderedMessageIdRef.current = latestMessageId;
    }, [messages, latestMessageId]);

    const handleMessageListScroll = () => {
        const list = listRef.current;
        if (!list) return;
        shouldStayAtBottomRef.current = list.scrollHeight - list.scrollTop - list.clientHeight < 100;
        if (list.scrollTop < 100 && hasOlderMessagesRef.current) loadOlderMessages();
    };

    const renderMessages = useMemo(
        () =>
            messages.map((message) => {
                const normalized = {
                    ...message,
                    dateAndTime: message.createdAt,
                    user: typeof message.user === 'object' ? message.user : { id: message.user },
                };
                normalized.id = message.id || message._id;
                messageRefs.current[normalized.id] = messageRefs.current[normalized.id] || React.createRef();
                const sharedProps = {
                    m: normalized,
                    rId: roomId,
                    teamData: {},
                    setReply: () => {},
                    refs: messageRefs.current,
                    handleClick: () => {},
                    handleMessageClick: () => {},
                    currUser: currentUser || {},
                    handleDeleteMessage: () => {},
                    sendEditedMessage: () => {},
                    roomType: channelInfo?.type,
                    userRole: currentUser?.role,
                    showMessageReactions: false,
                    hideActions: true,
                };
                return normalized.user?.id?.toString() === userId?.toString() ||
                    normalized.user?._id?.toString() === userId?.toString() ? (
                    <SenderMessage key={normalized.id} {...sharedProps} isForm={false} />
                ) : (
                    <ReceiverMessage key={normalized.id} {...sharedProps} isForm={false} />
                );
            }),
        [messages, roomId, currentUser, channelInfo, userId]
    );

    const sendMessage = async (message) => {
        if (!message || sending) return false;
        setSending(true);
        setSendError('');
        try {
            await conferenceApi.postChatMessage(conferenceId, message);
            shouldStayAtBottomRef.current = true;
            await fetchMessages();
            return true;
        } catch (error) {
            setSendError(error?.message || 'Message could not be sent. Try again.');
            return false;
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="conferenceChat">
            <div
                className="conferenceChat__messages"
                ref={listRef}
                onScroll={handleMessageListScroll}
                aria-live="polite"
            >
                <div className="conferenceChat__scrollContent" ref={scrollContentRef}>
                    {loadingOlder && (
                        <div className="conferenceChat__loadingOlder" role="status">
                            <VscLoading className="spin" aria-hidden="true" />
                            <span>Loading earlier messages…</span>
                        </div>
                    )}
                    {loading && messages.length === 0 ? (
                        <div className="conferenceChat__empty">Loading channel messages…</div>
                    ) : messages.length === 0 && !fetchError ? (
                        <div className="conferenceChat__empty">No messages yet. Start the conversation.</div>
                    ) : messages.length > 0 ? (
                        renderMessages
                    ) : null}
                </div>
            </div>
            <div className="conferenceChat__composer">
                <ConferenceChatInput onSend={sendMessage} isSending={sending || !roomId} />
            </div>
            {fetchError && (
                <div>
                    <span className="badge badge-danger">{fetchError}</span>
                </div>
            )}
            {sendError && (
                <div>
                    <span className="badge badge-danger">{sendError}</span>
                </div>
            )}
        </div>
    );
};

export default ConferenceChat;
